import { ArticleWithRelations, RequestWithRelations } from '@/models/request';
import { PaymentStepMessage } from '@/routes/Requests/components/StepMessage';
import { AcceptDispatchProposalTaskMessage } from '@/routes/Requests/Request/Article/components/ArticleTaskOrStepMessage/AcceptDispatchProposalTaskMessage';

import { AcceptDispatchTaskMessage } from './AcceptDispatchTaskMessage';
import { AcceptRequalificationTaskMessage } from './AcceptRequalificationTaskMessage';
import { AddCustomsInformationsTaskMessage } from './AddCustomsInformationsTaskMessage';
import { AdjustServiceDuringProcessingTaskMessage } from './AdjustServiceDuringProcessingTaskMessage';
import { AdjustServiceDuringQualityControlTaskMessage } from './AdjustServiceDuringQualityControlTaskMessage';
import { AnalysisStepMessage } from './AnalysisStepMessage';
import { AnalyzeArticleTaskMessage } from './AnalyzeArticleTaskMessage';
import { ArticleArchivedMessage } from './ArticleArchivedMessage';
import { ChooseArticleServiceTaskMessage } from './ChooseArticleServiceTaskMessage';
import { ConfirmExchangeStockAvailabilityTaskMessage } from './ConfirmExchangeStockAvailabilityTaskMessage';
import { ContactClientTaskMessage } from './ContactClientTaskMessage';
import { CreateShipmentTaskMessage } from './CreateShipmentTaskMessage';
import { DropoffShipmentTaskMessage } from './DropoffShipmentTaskMessage';
import { ExchangeArticleTaskMessage } from './ExchangeArticleTaskMessage';
import { ExchangeStepMessage } from './ExchangeStepMessage';
import { FinaliseShipmentTaskMessage } from './FinaliseShipmentTaskMessage';
import { HandleShipmentIssueTaskMessage } from './HandleShipmentIssueTaskMessage';
import { PrepareShipmentTaskMessage } from './PrepareShipmentTaskMessage';
import { ProcessingStepMessage } from './ProcessingStepMessage';
import { QualityControlStepMessage } from './QualityControlStepMessage';
import { QualityControlTaskMessage } from './QualityControlTaskMessage';
import { RefundStepMessage } from './RefundStepMessage';
import { RepairArticleTaskMessage } from './RepairArticleTaskMessage';
import { RepairStepMessage } from './RepairStepMessage';
import { ServiceChoiceStepMessage } from './ServiceChoiceStepMessage';
import { StartArticleExchangeTaskMessage } from './StartArticleExchangeTaskMessage';
import { StartArticleRepairTaskMessage } from './StartArticleRepairTaskMessage';
import { TransitStepMessage } from './TransitStepMessage';
import { ValidateExternalPaymentTaskMessage } from './ValidateExternalPaymentTaskMessage';
import { ValidateExternalRefundTaskMessage } from './ValidateExternalRefundTaskMessage';
import { ValidateTransitTaskMessage } from './ValidateTransitTaskMessage';
import { ValidationStepMessage } from './ValidationStepMessage';
import { VerifyShipmentTaskMessage } from './VerifyShipmentTaskMessage';

const ArticleTaskOrStepMessage = ({
  article,
  request,
}: {
  article: ArticleWithRelations;
  request: RequestWithRelations;
}) => {
  if (article.archivedAt) {
    return <ArticleArchivedMessage article={article} />;
  }

  if (article.taskType) {
    switch (article.taskType) {
      case 'accept_dispatch':
        return <AcceptDispatchTaskMessage />;
      case 'accept_dispatch_proposal':
        return <AcceptDispatchProposalTaskMessage article={article} />;
      case 'accept_requalification':
        return <AcceptRequalificationTaskMessage article={article} />;
      case 'add_customs_informations':
        return <AddCustomsInformationsTaskMessage article={article} />;
      case 'adjust_service_during_quality_control':
        return <AdjustServiceDuringQualityControlTaskMessage article={article} />;
      case 'adjust_service_during_processing':
        return <AdjustServiceDuringProcessingTaskMessage article={article} />;
      case 'analyze_article':
        return <AnalyzeArticleTaskMessage />;
      case 'choose_article_service':
        return <ChooseArticleServiceTaskMessage article={article} />;
      case 'confirm_exchange_stock_availability':
        return <ConfirmExchangeStockAvailabilityTaskMessage />;
      case 'contact_client':
        return <ContactClientTaskMessage article={article} />;
      case 'control_quality':
        return <QualityControlTaskMessage article={article} />;
      case 'create_shipment':
        return <CreateShipmentTaskMessage article={article} />;
      case 'dropoff_shipment':
        return <DropoffShipmentTaskMessage article={article} />;
      case 'exchange_article':
        return <ExchangeArticleTaskMessage />;
      case 'finalise_shipment':
        return <FinaliseShipmentTaskMessage article={article} />;
      case 'handle_shipment_issue':
        return <HandleShipmentIssueTaskMessage article={article} />;
      case 'prepare_shipment':
        return <PrepareShipmentTaskMessage article={article} />;
      case 'repair_article':
        return <RepairArticleTaskMessage article={article} />;
      case 'start_article_exchange':
        return <StartArticleExchangeTaskMessage />;
      case 'start_article_repair':
        return <StartArticleRepairTaskMessage />;
      case 'validate_payment':
        return <ValidateExternalPaymentTaskMessage article={article} />;
      case 'validate_refund':
        return <ValidateExternalRefundTaskMessage article={article} />;
      case 'validate_transit':
        return <ValidateTransitTaskMessage article={article} />;
      case 'verify_shipment_reception':
        return <VerifyShipmentTaskMessage article={article} />;
      default:
        return null;
    }
  }

  // To simplify when TECH-898 is done, the status should be in the step directly
  const stepStatus = article.steps?.find((step) => step.id === article.step?.id)?.status;

  if (article.step && !article.step.sync) {
    switch (article.step.step) {
      case 'service-choice':
        return <ServiceChoiceStepMessage article={article} />;
      case 'transit':
        return <TransitStepMessage article={article} />;
      case 'analysis':
        return <AnalysisStepMessage article={article} />;
      case 'repair':
        return <RepairStepMessage article={article} />;
      case 'exchange':
        return <ExchangeStepMessage article={article} />;
      case 'quality-control':
        return <QualityControlStepMessage article={article} />;
      case 'processing':
        return <ProcessingStepMessage article={article} />;
      case 'validation':
        return stepStatus !== 'pending' ? <ValidationStepMessage /> : null;
      case 'payment':
        return stepStatus !== 'pending' ? (
          <PaymentStepMessage article={article} request={request} />
        ) : null;
      case 'refund':
        return stepStatus !== 'pending' ? <RefundStepMessage request={request} /> : null;
      default:
        return null;
    }
  }

  return null;
};

export default ArticleTaskOrStepMessage;
